import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge, BmModal, Btn, Card, Checkbox, ConfirmModal, EmptyState, Loading, Overline } from '../../ForBrands/ui/BrandUI';
import { Icon } from '../../ForBrands/ui/icons';
import CascadePicker from '../../ForBusiness/Cacheshare/CascadePicker';
import { resolveFetchPlan, legacySportKey } from '../../ForBusiness/Cacheshare/taxonomy';
import { executeFetchPlan } from '../../ForBusiness/Cacheshare/services/cacheShareServices';
import {
  adminAssignAthleteTeam,
  fetchOnboardingCandidates,
} from '../../auth/Onboarding/services/onboardingServices';
import { displayTeamName, stripSeasonSuffix } from '../../../utils/teamNames';
import { API_BASE_URL } from '../../../constants';
import { pickedTeamBinding } from '../../ForCoaches/v2/onboarding/coachOnboardingModel';

const adminPost = async (path, payload) => {
  const token = localStorage.getItem('token');
  const res = await axios.post(
    `${API_BASE_URL}/${path}`,
    payload,
    { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' } },
  );
  return res.data || {};
};

// Admin: add a team to the athlete's teams, with no name check: beside
// their main team, or as the main team when they have none. Counts as
// verified. Answers {teams}.
export const adminAddAthleteTeam = (email, payload) => adminPost(`admin/athletes/${encodeURIComponent(email)}/teams/`, payload);

// Admin: the athlete's Club (or Team) field picks a real team (notes of
// 10/19, K4): the team becomes a binding (the main team when they have
// none, else another team) and the field is written from it. payload as
// adminAddAthleteTeam's plus slot 'club' | 'team'. Answers {teams, slot,
// bound, fillNote?}.
export const adminSetAthleteTeamSlot = (email, payload) => adminPost(`admin/athletes/${encodeURIComponent(email)}/teams/slot/`, payload);

const ADD_TEAM_ERRORS = {
  no_primary: 'This athlete has no main team yet. Use Replace the main team to set one.',
  already_listed: "This team is already on the athlete's profile.",
  too_many_teams: 'An athlete can have up to 4 teams. Remove one first.',
  sport_mismatch: "This team plays a different sport from the athlete's main team.",
  gender_mismatch: "This team is for a different gender from the athlete's main team.",
  name_mismatch: 'The roster name does not match this athlete.',
  player_not_on_roster: "That player is not on this team's roster.",
  roster_unavailable: 'The roster could not be loaded. Try again.',
  profile_taken: 'Another athlete already holds that roster spot.',
};

// The server's sentence when it sends one, else a plain line for its code.
export const addTeamErrorMessage = (err) => {
  const data = err?.response?.data || {};
  if (typeof data.message === 'string' && data.message.trim()) return data.message;
  return ADD_TEAM_ERRORS[data.error] || 'Could not add the team.';
};

// A team said another gender than the profile keeps (fillConflicts /
// fillNote): the profile kept its gender, which the admin is told.
export const adminFillNote = (data) => (
  (Array.isArray(data?.fillConflicts) && data.fillConflicts.length) || (typeof data?.fillNote === 'string' && data.fillNote.trim())
    ? 'This team is listed for the other gender. The profile keeps the gender on file.'
    : ''
);

// The binding fields a team write sends: the league and source always, the
// squad key and the program-only flag when the row has them.
const bindingFields = (b) => ({
  league: b.league,
  ...(b.leagueKey ? { leagueKey: b.leagueKey } : {}),
  ...(b.programOnly ? { programOnly: true } : {}),
});

// How the picked team's roster was read (onboarding/candidates/): 'live',
// 'saved' (the team feed is down and the roster saved on {savedAt} stands
// in), 'down' (the feed did not answer and nothing is saved: 503
// provider_unavailable, another 5xx or no answer) or 'failed'.
const ROSTER_LIVE = { status: 'live', savedAt: null };
export const rosterReadOf = (data) => ({ status: data?.rosterStatus === 'saved' ? 'saved' : 'live', savedAt: data?.savedAt || null });
export const rosterFailureOf = (err) => {
  const status = err?.response?.status;
  const down = err?.response?.data?.code === 'provider_unavailable' || !status || status >= 500;
  return { status: down ? 'down' : 'failed', savedAt: null };
};
// "Oct 12, 2026", or '' when the time does not read.
const savedOn = (iso) => {
  const when = iso ? new Date(iso) : null;
  return when && !Number.isNaN(when.getTime()) ? when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
};

const EMPTY_FILTERS = {
  sport: null,
  gender: null,
  tier: null,
  region: null,
  body: null,
  subDivision: null,
  classDivision: null,
  conference: null,
  teamSearch: '',
};

const MODES = ['add', 'club', 'replace'];

// Admin support tool: add, set as club, or force-change (or clear) an
// athlete's team.
//   Step 1: pick the team via the onboarding cascade (real team_id/league).
//   Step 2: pick the athlete's record ON that team (sets player_id, which
//           is what drives live Performance scoring).
// This mirrors onboarding: a team alone isn't enough, the athlete must be
// matched to a specific player in the roster for stats to resolve.
// `initialMode` opens it on 'add' (Verify on a team), 'club' or 'replace'.
// Adding works without a main team: the team becomes the main team.
const AssignAthleteTeamDialog = ({ open, athlete, currentTeamName, onClose, onChange, initialMode }) => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  // How the roster was read (rosterReadOf / rosterFailureOf), and Try again.
  const [roster, setRoster] = useState(ROSTER_LIVE);
  const [rosterAttempt, setRosterAttempt] = useState(0);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [skipPlayer, setSkipPlayer] = useState(false); // "not on roster" escape
  const [submitting, setSubmitting] = useState(false);
  const [confirmAssign, setConfirmAssign] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  // 'add' adds this team to the athlete's teams (beside the main team, or
  // as the main team when there is none); 'club' does that and fills their
  // Club field with it; 'replace' moves the athlete's main team.
  const hasPrimary = !!athlete?.team_id;
  const startMode = () => (MODES.includes(initialMode) ? initialMode : hasPrimary ? 'add' : 'replace');
  const [mode, setMode] = useState(startMode);

  const fetchPlan = useMemo(() => resolveFetchPlan({
    sportValue: filters.sport?.value,
    gender: filters.gender?.value,
    tier: filters.tier?.value,
    region: filters.region?.value,
    body: filters.body,
    subDivision: filters.subDivision,
    classDivision: filters.classDivision,
    conference: filters.conference?.value,
  }), [filters]);

  // Where the picked team is filed and read: the row's own squad, never the
  // scope's first key (pickedTeamBinding).
  const binding = useMemo(() => pickedTeamBinding(selectedTeam, fetchPlan), [selectedTeam, fetchPlan]);
  const resolvedLeague = selectedTeam ? binding.league : '';
  const programOnly = !!selectedTeam && binding.programOnly;

  // Cascade-driven team fetch.
  useEffect(() => {
    if (!open || !fetchPlan) { setTeams([]); return; }
    let cancelled = false;
    setTeamsLoading(true);
    // Every saved team, not only those with stats: this binds a team, and
    // a provider that is paused (football while Genius is down) lists only
    // its scored teams otherwise.
    executeFetchPlan(fetchPlan, { allTeams: true })
      .then((rows) => { if (!cancelled) setTeams(rows || []); })
      .catch(() => { if (!cancelled) setTeams([]); })
      .finally(() => { if (!cancelled) setTeamsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(fetchPlan)]);

  // Once a team is picked, load its roster ranked against the athlete's name
  // so the admin can pick the matching player (sets player_id). A program
  // with no competition has no roster: the team is added on its own. While
  // the team feed is down a saved roster stands in ("Roster saved <date>"),
  // else the roster says it is not available, with Try again, and the team
  // can be added without a spot for the athlete to pick later.
  useEffect(() => {
    setSelectedCandidate(null);
    setSkipPlayer(programOnly);
    setRoster(ROSTER_LIVE);
    if (!open || !selectedTeam?.id || !resolvedLeague) { setCandidates([]); return; }
    let cancelled = false;
    setCandidatesLoading(true);
    fetchOnboardingCandidates({
      league: resolvedLeague,
      teamId: selectedTeam.id,
      firstName: athlete?.name || '',
      lastName: athlete?.surname || '',
    })
      .then((d) => { if (!cancelled) { setCandidates(d?.candidates || []); setRoster(rosterReadOf(d)); } })
      .catch((err) => { if (!cancelled) { setCandidates([]); setRoster(rosterFailureOf(err)); } })
      .finally(() => { if (!cancelled) setCandidatesLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedTeam?.id, resolvedLeague, programOnly, athlete?.name, athlete?.surname, rosterAttempt]);

  // Reset when the dialog opens/closes or the athlete swaps.
  useEffect(() => {
    if (!open) {
      setFilters(EMPTY_FILTERS);
      setSelectedTeam(null);
      setCandidates([]);
      setSelectedCandidate(null);
      setSkipPlayer(false);
      setConfirmAssign(false);
      setConfirmClear(false);
    }
    setMode(startMode());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, athlete?.email, athlete?.team_id, initialMode]);

  const athleteLabel = athlete?.name
    ? `${athlete.name} ${athlete.surname || ''}`.trim()
    : (athlete?.email || '');

  const canSubmit = !!selectedTeam?.id && (!!selectedCandidate || skipPlayer);
  // The name the team is stored under, and the one people read (a squad's
  // age group, no season).
  const selectedTeamName = selectedTeam ? (selectedTeam.name || selectedTeam.market || '') : '';
  const shownTeamName = selectedTeam ? displayTeamName({ ...selectedTeam, teamName: selectedTeamName }) || selectedTeamName : '';
  const leagueLabel = stripSeasonSuffix(
    selectedTeam?.leagueName || [filters.body?.name, filters.subDivision?.name].filter(Boolean).join(' ') || '',
  );
  const otherTeams = (Array.isArray(athlete?.teams) ? athlete.teams : [])
    .filter((t) => t && String(t.teamId || '') !== String(athlete?.team_id || ''))
    .map((t) => (t.teamName ? displayTeamName(t) : ''))
    .filter(Boolean);

  const finished = (message, data) => {
    toast.success(message);
    const note = adminFillNote(data);
    if (note) toast.info(note);
    onChange?.();
    onClose?.();
  };

  // The team fields an add and a slot write send. A team added with no spot
  // while its roster could not be read waits for the athlete to pick the
  // spot (pendingRosterPick: the edit hub asks them).
  const teamPayload = () => ({
    ...bindingFields(binding),
    teamId: String(selectedTeam.id),
    teamSource: binding.teamSource,
    teamName: selectedTeamName,
    leagueLabel,
    playerId: selectedCandidate?.playerId || '',
    ...(!selectedCandidate && roster.status === 'down' ? { pendingRosterPick: true } : {}),
  });

  const canWrite = () => {
    if (!selectedTeam?.id) return false;
    if (!resolvedLeague && !programOnly) { toast.error('Could not resolve the league key.'); return false; }
    return true;
  };

  const submitAdd = async () => {
    if (!canWrite()) return;
    setSubmitting(true);
    try {
      const data = await adminAddAthleteTeam(athlete.email, teamPayload());
      finished(hasPrimary
        ? `Added ${shownTeamName} as another team for ${athleteLabel}. Their main team stays the same.`
        : `Added ${shownTeamName} as ${athleteLabel}'s team.`, data);
    } catch (err) {
      toast.error(addTeamErrorMessage(err));
    } finally {
      setSubmitting(false);
      setConfirmAssign(false);
    }
  };

  const submitClub = async () => {
    if (!canWrite()) return;
    setSubmitting(true);
    try {
      const data = await adminSetAthleteTeamSlot(athlete.email, { ...teamPayload(), slot: 'club' });
      finished(`Set ${shownTeamName} as ${athleteLabel}'s club.`, data);
    } catch (err) {
      toast.error(addTeamErrorMessage(err));
    } finally {
      setSubmitting(false);
      setConfirmAssign(false);
    }
  };

  const submitAssign = async () => {
    if (!canWrite()) return;
    setSubmitting(true);
    try {
      const payload = {
        teamId: selectedTeam.id,
        ...bindingFields(binding),
        teamSource: binding.teamSource,
        currentTeam: selectedTeamName,
        playerId: selectedCandidate?.playerId || '',
        sport: legacySportKey(filters.sport?.value, filters.gender?.value),
        cascade: {
          sportLabel: filters.sport?.name || '',
          genderLabel: filters.gender?.name || '',
          tierLabel: filters.tier?.name || '',
          regionLabel: filters.region?.name || '',
          bodyLabel: filters.body?.name || '',
          subDivisionLabel: filters.subDivision?.name || '',
          classDivisionLabel: filters.classDivision?.name || '',
          conferenceLabel: filters.conference?.name || '',
          leagueDisplayName: stripSeasonSuffix(selectedTeam.leagueName || ''),
        },
      };
      const data = await adminAssignAthleteTeam(athlete.email, payload);
      finished(`Team set to ${shownTeamName} for ${athleteLabel}.`, data);
    } catch (err) {
      const data = err?.response?.data || {};
      toast.error(data.message || data.error || 'Could not assign team.');
    } finally {
      setSubmitting(false);
      setConfirmAssign(false);
    }
  };

  // Runs once the admin confirms in the Clear team ConfirmModal.
  const submitClear = async () => {
    setSubmitting(true);
    try {
      await adminAssignAthleteTeam(athlete.email, { clear: true });
      toast.success(`Team cleared for ${athleteLabel}.`);
      onChange?.();
      onClose?.();
    } catch (err) {
      const data = err?.response?.data || {};
      toast.error(data.message || data.error || 'Could not clear team.');
    } finally {
      setSubmitting(false);
      setConfirmClear(false);
    }
  };

  if (!open || !athlete) return null;

  const candidateName = (c) => (c ? (c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim()) : '');
  const primaryLabel = !selectedTeam
    ? 'Pick a team'
    : (!selectedCandidate && !skipPlayer) ? 'Select the athlete' : { add: 'Add team', club: 'Set as club', replace: 'Assign team' }[mode];

  const modeOptions = [
    {
      value: 'add',
      label: hasPrimary ? 'Add as another team' : 'Add as their team',
      hint: hasPrimary
        ? `Keeps ${currentTeamName || 'the main team'} as the main team. Their games count toward the CacheScore. Counts as verified.`
        : 'It becomes their main team. Their games count toward the CacheScore. Counts as verified.',
    },
    {
      value: 'club',
      label: 'Set as their club',
      hint: hasPrimary
        ? 'Adds it to their teams and fills their Club field with it. Their games count toward the CacheScore. Counts as verified.'
        : 'It becomes their main team and fills their Club field. Their games count toward the CacheScore. Counts as verified.',
    },
    {
      value: 'replace',
      label: hasPrimary ? 'Replace the main team' : 'Assign the main team',
      hint: hasPrimary ? 'Moves the athlete off their main team and clears the coach link on it.' : 'Sets their main team through the full team assignment.',
    },
  ];

  const confirmBody = () => {
    const as = selectedCandidate ? <> as <span className="font-semibold">{candidateName(selectedCandidate)}</span></> : null;
    if (mode === 'replace') {
      return (
        <>
          <p className="text-bm-ink">
            Set <span className="font-semibold">{athleteLabel}</span>'s team to{' '}
            <span className="font-semibold">{shownTeamName}</span>
            {as}?
          </p>
          <p className="mt-2 bm-body-s">
            {selectedCandidate
              ? "Live Performance and Game Stats will pull from this player's record."
              : "No roster player selected, the team is set but Performance won't have a stat source."}
            {' '}This also clears any existing coach verification link.
          </p>
        </>
      );
    }
    const club = mode === 'club';
    return (
      <>
        <p className="text-bm-ink">
          {club ? 'Set ' : 'Add '}<span className="font-semibold">{shownTeamName}</span>
          {club ? <> as <span className="font-semibold">{athleteLabel}</span>'s club</> : <>{hasPrimary ? ' as another team for ' : ' as the team of '}<span className="font-semibold">{athleteLabel}</span></>}
          {as}?
        </p>
        <p className="mt-2 bm-body-s">
          {hasPrimary
            ? `Their main team stays ${currentTeamName || 'as it is'}. Their games count toward the CacheScore.`
            : 'It becomes their main team. Their games count toward the CacheScore.'}
          {' '}An admin-added team counts as verified. No name check is run.
        </p>
      </>
    );
  };

  const onConfirm = { add: submitAdd, club: submitClub, replace: submitAssign }[mode];

  return (
    <>
      {/* A confirm opens as a second BmModal on top, so Escape and its
          backdrop close only the confirm. */}
      <BmModal
        title="Assign or change team"
        onClose={onClose}
        width={760}
        footer={(
          <>
            <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
            <Btn onClick={() => setConfirmAssign(true)} disabled={!canSubmit || submitting}>{primaryLabel}</Btn>
          </>
        )}
      >
        <p className="bm-body-s text-bm-muted">{athleteLabel} · pick the team, then the athlete on it</p>

        <div className="mt-5 flex flex-col gap-6">
          {/* Current team */}
          <section className="flex flex-col gap-2">
            <Overline>Current team</Overline>
            <Card padding="px-4 py-3" className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="bm-label-l text-bm-ink">{currentTeamName || 'No team assigned'}</div>
                {otherTeams.length > 0 && (
                  <div className="mt-0.5 bm-body-s text-bm-muted">Other teams: {otherTeams.join(', ')}</div>
                )}
              </div>
              {currentTeamName && (
                <Btn size="sm" variant="danger" onClick={() => setConfirmClear(true)} disabled={submitting}>
                  <Icon name="trash" size={14} className="mr-1.5" />
                  Clear team
                </Btn>
              )}
            </Card>
          </section>

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 bm-overline">What to do</legend>
            {modeOptions.map((opt) => {
              const active = mode === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-[8px] border px-4 py-3 transition-colors ${
                    active ? 'border-bm-orange bg-bm-orange/5' : 'border-bm-border bg-white hover:bg-bm-subtle'
                  }`}
                >
                  <input
                    type="radio"
                    name="assign-mode"
                    value={opt.value}
                    checked={active}
                    onChange={() => setMode(opt.value)}
                    className="mt-1 accent-bm-orange"
                  />
                  <span className="min-w-0">
                    <span className="block bm-label-l text-bm-ink">{opt.label}</span>
                    <span className="mt-0.5 block bm-body-s text-bm-muted">{opt.hint}</span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          {/* Step 1: team picker */}
          <section className="flex flex-col gap-2">
            <Overline>Pick the team</Overline>
            <Card padding="p-5">
              <CascadePicker
                filters={filters}
                onFiltersChange={setFilters}
                teams={teams}
                selectedTeam={selectedTeam}
                onTeamSelect={setSelectedTeam}
                isLoadingTeams={teamsLoading}
                layout="modal"
                variant="v2"
              />
            </Card>
          </section>

          {/* Step 2: pick the athlete on the team */}
          {selectedTeam?.id && (
            <section className="flex flex-col gap-2">
              <Overline>Pick the athlete on {shownTeamName}</Overline>
              {programOnly ? (
                <p className="bm-body-s text-bm-muted">
                  This program has no competition on file, so there is no roster to pick from. It is added through a provisional CACHE team, listed for review in Team Management.
                </p>
              ) : !resolvedLeague ? (
                // No league to file it under: the program's competitions
                // could not be checked (the team feed did not answer).
                <p className="bm-body-s text-bm-muted">
                  This team's league could not be checked just now, so it cannot be added yet. Try again in a minute.
                </p>
              ) : (
                <>
                  <p className="bm-body-s text-bm-muted">
                    This matches the athlete to a player on the roster so live Performance and Game Stats pull from their record.
                  </p>
                  {candidatesLoading ? (
                    <Loading label="Loading roster..." />
                  ) : (
                    <>
                      {roster.status === 'down' || roster.status === 'failed' ? (
                        <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-3">
                          <p className="bm-body-s text-bm-muted">
                            {roster.status === 'failed'
                              ? 'We could not load this roster. Try again.'
                              : mode === 'replace'
                                ? 'The roster is not available right now. Try again in a minute.'
                                : 'The roster is not available right now. Try again in a minute, or add the team only and the athlete picks their spot later.'}
                          </p>
                          <Btn variant="secondary" size="sm" onClick={() => setRosterAttempt((n) => n + 1)}>Try again</Btn>
                        </div>
                      ) : candidates.length === 0 ? (
                        <EmptyState>No roster records found for this team.</EmptyState>
                      ) : (
                        <>
                          {roster.status === 'saved' && (
                            <p className="bm-body-s text-bm-muted">
                              {savedOn(roster.savedAt) ? `Roster saved ${savedOn(roster.savedAt)}.` : 'Roster saved earlier.'} The live roster is not available right now.
                            </p>
                          )}
                          <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto">
                            {candidates.map((c) => {
                              const isSelected = selectedCandidate?.playerId === c.playerId;
                              const meta = [c.position, c.jersey && `#${c.jersey}`, c.classStanding].filter(Boolean);
                              return (
                                <li key={c.playerId}>
                                  <button
                                    type="button"
                                    aria-pressed={isSelected}
                                    onClick={() => { setSelectedCandidate(c); setSkipPlayer(false); }}
                                    className={`flex w-full items-center justify-between gap-3 rounded-[8px] border px-4 py-3 text-left transition-colors ${
                                      isSelected ? 'border-bm-orange bg-bm-orange/5' : 'border-bm-border bg-white hover:bg-bm-subtle'
                                    }`}
                                  >
                                    <span className="flex min-w-0 flex-wrap items-center gap-2">
                                      <span className="truncate bm-label-l text-bm-ink">{candidateName(c)}</span>
                                      {meta.length > 0 && (
                                        <span className="truncate bm-body-s text-bm-muted">{meta.join(' · ')}</span>
                                      )}
                                      {typeof c.matchScore === 'number' && c.matchScore >= 85 && (
                                        <Badge tone="outline" dot="#1f9d55">Best match</Badge>
                                      )}
                                    </span>
                                    {isSelected && <Icon name="check" className="text-bm-orange" />}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </>
                      )}
                      <Checkbox
                        checked={skipPlayer}
                        onChange={(next) => { setSkipPlayer(next); if (next) setSelectedCandidate(null); }}
                        label={mode === 'replace'
                          ? 'This athlete is not on the roster. Set the team only (no stats).'
                          : roster.status === 'down'
                            ? 'Add the team only. The athlete picks their roster spot later.'
                            : 'This athlete is not on the roster. Add the team only.'}
                        className="mt-1"
                      />
                    </>
                  )}
                </>
              )}
            </section>
          )}
        </div>
      </BmModal>

      {/* Replacing a team is a force-change with side effects, and adding
          one changes the profile, so the admin confirms before the write.
          Only the replace clears data (the coach verification link), so
          only it asks for the acknowledgment. */}
      {confirmAssign && selectedTeam && (
        <ConfirmModal
          title={{ add: 'Confirm new team', club: 'Confirm club', replace: 'Confirm team change' }[mode]}
          body={confirmBody()}
          ackLabel="I understand this clears their coach verification link."
          requireAck={mode === 'replace'}
          cancelLabel={mode === 'replace' ? undefined : 'Cancel'}
          confirmLabel={{ add: 'Yes, add team', club: 'Yes, set as club', replace: 'Yes, change team' }[mode]}
          busy={submitting}
          onConfirm={onConfirm}
          onClose={() => { if (!submitting) setConfirmAssign(false); }}
        />
      )}

      {confirmClear && (
        <ConfirmModal
          title="Clear team"
          body={`Remove the team from ${athleteLabel}? This also clears their player match and coach verification link.`}
          confirmLabel="Yes, clear team"
          tone="danger"
          busy={submitting}
          onConfirm={submitClear}
          onClose={() => { if (!submitting) setConfirmClear(false); }}
        />
      )}
    </>
  );
};

export default AssignAthleteTeamDialog;
